// src/services/userService.js
// Service for handling User API calls

import axiosInstance from './axiosConfig';

const userService = {
  /**
   * Get all users (optionally with pagination if backend supports it)
   * @param {number} page - Page number
   * @param {number} limit - Items per page
   * @returns {Promise<Array>} Array of user objects
   */
  getAll: async (page = 1, limit = 10) => {
    const response = await axiosInstance.get(`/api/v1/auth/users?page=${page}&limit=${limit}`);
    // If backend returns { success, message, data: [...] }
    return Array.isArray(response.data)
      ? response.data
      : response.data.data;
  },

  /**
   * Get all users with pagination metadata
   * @param {number} page - Page number
   * @param {number} limit - Items per page
   * @returns {Promise<Object>} Object with pagination data
   */
  getAllWithPagination: async (page = 1, limit = 10) => {
    const response = await axiosInstance.get(`/api/v1/auth/users?page=${page}&limit=${limit}`);
    return response.data;
  },

  /**
   * Get a single user by ID
   * @param {number} id - User ID
   * @returns {Promise<Object>} User object
   */
  getById: async (id) => {
    const response = await axiosInstance.get(`/api/v1/auth/users/${id}`);
    return response.data.data || response.data;
  },

  /**
   * Get active users for dropdown selection
   * @returns {Promise<Array>} Array of {value, text} objects
   */
  getActiveUsers: async () => {
    const response = await axiosInstance.get("/api/v1/auth/users/active");
    return response.data.data.map(user => ({
      value: user.id,
      text: user.username,
    }));
  },

  /**
   * Get all employees for dropdown selection
   * @returns {Promise<Array>} Array of employee objects
   */
  getEmployees: async () => {
    const response = await axiosInstance.get('/employees');
    const employeeData = Array.isArray(response.data) 
      ? response.data 
      : response.data.data || [];
    return employeeData.map(employee => ({
      value: employee.id,
      text: `${employee.fullName} (${employee.employeeCode})`,
      fullName: employee.fullName,
      employeeCode: employee.employeeCode
    }));
  },

  /**
   * Create a new user (Register)
   * POST /api/v1/auth/register
   * @param {Object} userData - User data
   * {
   *   "username": "sanduni.fernando",
   *   "email": "sanduni.fernando@company.lk",
   *   "password": "Sanduni@123",
   *   "isActive": true,
   *   "role": "ADMIN",
   *   "employeeId": 9
   * }
   * @returns {Promise<Object>} Created user object
   */
  create: async (userData) => {
    const response = await axiosInstance.post('/auth/register', userData);
    return response.data.data || response.data;
  },

  /**
   * Bulk user creation (if you ever need array input)
   * @param {Array} usersArray - Array of user objects
   * @returns {Promise<Object>} Created users
   */
  createBulk: async (usersArray) => {
    const response = await axiosInstance.post('/api/v1/auth/register/bulk', {
      users: usersArray,
    });
    return response.data.data || response.data;
  },

  /**
   * Update an existing user
   * PUT /api/v1/auth/users/{id}
   * @param {number} id - User ID
   * @param {Object} userData - Updated user data
   * @returns {Promise<Object>} Updated user object
   */
  update: async (id, userData) => {
    const response = await axiosInstance.put(`/api/v1/auth/users/${id}`, userData);
    return response.data.data || response.data;
  },

  /**
   * Delete a user
   * DELETE /api/v1/auth/users/{id}
   * @param {number} id - User ID
   * @returns {Promise<void>}
   */
  delete: async (id) => {
    const response = await axiosInstance.delete(`/api/v1/auth/users/${id}`);
    return response.data;
  },

  /**
   * Filter users with search and pagination
   * @param {Object} filters - Filter parameters
   * @param {number} page - Page number
   * @param {number} limit - Items per page
   * @returns {Promise<Array>} Filtered array of users
   */
  filter: async (filters = {}, page = 1, limit = 10) => {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('limit', limit);
    if (filters.search) params.append('search', filters.search);
    if (filters.role) params.append('role', filters.role);
    if (filters.isActive !== undefined) params.append('isActive', filters.isActive);

    const response = await axiosInstance.get(`/api/v1/auth/users?${params.toString()}`);
    return Array.isArray(response.data)
      ? response.data
      : response.data.data;
  },
};

export default userService;